import React from 'react';
import { Text } from '@billda/design-system';
import { BilldaLogo } from '../../atoms/BilldaLogo';

interface BrandSectionProps {
  /**
   * Variant determines the tagline text
   * @default 'login'
   */
  variant?: 'login' | 'invite';
  /**
   * Organization name to display (for invite variant)
   */
  organizationName?: string;
}

/**
 * Molecule: Billda logo + tagline text combination
 * Centered flex column for branded side
 * Always uses white logo on gradient background
 */
export const BrandSection: React.FC<BrandSectionProps> = ({
  variant = 'login',
  organizationName,
}) => {
  // Always use white logo on dark background
  const logoVariant = 'secondary';

  if (variant === 'invite') {
    return (
      <div className="relative z-[2] flex flex-col items-center justify-center gap-4 px-8 text-center">
        <BilldaLogo variant={logoVariant} width={240} />

        {organizationName && (
          <Text
            as="p"
            variant="h3"
            className="max-w-[500px] font-semibold text-on-inverse"
          >
            {organizationName}
          </Text>
        )}

        <Text
          as="p"
          variant="h5"
          className="max-w-[500px] font-medium text-on-inverse"
        >
          Welcome to your team!
        </Text>
      </div>
    );
  }

  return (
    <div className="relative z-[2] mx-auto flex max-w-[520px] flex-col items-center justify-center gap-6 px-12 text-center">
      <BilldaLogo variant={logoVariant} width={200} />

      {/* Badge — amber on dark for strong contrast */}
      <span className="inline-block rounded-full border border-border-primary-strong bg-surface-primary-soft px-4 py-1.5">
        <Text
          variant="caption"
          className="text-surface-primary text-[0.7rem] font-bold tracking-[0.07em] uppercase"
        >
          Managing sub-contractors for UK housebuilders
        </Text>
      </span>

      {/* Headline */}
      <div className="-mt-2">
        <Text
          as="p"
          variant="h3"
          className="font-bold leading-[1.15] text-on-inverse"
        >
          One platform for your whole operation.
        </Text>
        <Text
          as="p"
          variant="h3"
          className="mt-1 font-bold leading-[1.15] text-surface-primary"
        >
          From site to payday.
        </Text>
      </div>

      {/* Subline */}
      <Text as="p" variant="body1" className="leading-[1.65] text-on-inverse">
        Billda gives professional sub-contractors the tools to manage price
        work, gangs, day work, and weekly pay. No spreadsheets, no WhatsApp
        chains, no Sunday evening chaos.
      </Text>
    </div>
  );
};
